import React, { useState } from 'react';
import EmptyState from '../util/EmptyState';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import TableSortLabel from '@mui/material/TableSortLabel';
import { t } from 'i18next';
import moment from 'moment';
import Checkbox from '@mui/material/Checkbox';
import Paginations from '../util/Paginations';
import DeleteArrayAll from '../modal/DeleteArrayAll';
import CompanyDetail from './CompanyDetail';
import { MODULES } from './adminConstants';

const StyledHeadCell = styled(TableCell)(() => ({
  color: 'var(--text-color)',
  fontSize: 11,
  fontWeight: 600,
  padding: '10px 13px',
  backgroundColor: 'var(--background-color)',
  borderBottom: 'var(--border-input)',
  whiteSpace: 'nowrap',
}));

const StyledBodyCell = styled(TableCell)(() => ({
  color: 'var(--text-color)',
  fontSize: 13,
  padding: '7px 13px',
  backgroundColor: 'var(--btn-user-form)',
  borderBottom: 'var(--border-input)',
  verticalAlign: 'middle',
}));

interface Props {
  companyRow: any[];
  selected: readonly number[];
  setSelected: (v: readonly number[]) => void;
  baseUrl: string;
  refreshCompany: () => void;
  tariffRow: any[];
}

function getPaidUntilStatus(date?: string | null) {
  if (!date) return null;
  const d = moment(date);
  const now = moment();
  const daysLeft = d.diff(now, 'days');
  if (daysLeft < 0)  return 'expired';
  if (daysLeft <= 14) return 'warning';
  return 'active';
}

function PaidUntilBadge({ date }: { date?: string | null }) {
  if (!date) return <span className="opacity-40 text-[12px]">—</span>;
  const status = getPaidUntilStatus(date);
  const label = moment(date).format('DD.MM.YYYY');
  const styles: Record<string, string> = {
    active:  'bg-[var(--color-success-bg,#e8f5e9)] text-[var(--color-success,#2e7d32)]',
    warning: 'bg-[var(--color-warning-bg,#fff3e0)] text-[var(--color-warning,#e65100)]',
    expired: 'bg-[var(--color-danger-bg,#ffebee)]  text-[var(--color-danger,#c62828)]',
  };
  return (
    <span className={`inline-block px-[7px] py-[1px] rounded-[10px] text-[11px] font-medium ${styles[status!]}`}>
      {label}
    </span>
  );
}

function ModuleChips({ modules, tariff }: { modules: string[] | null; tariff: any }) {
  const active = new Set<string>(modules ?? []);
  const shown = MODULES.filter(m => active.has(m)).slice(0, 3);
  const rest = MODULES.filter(m => active.has(m)).length - shown.length;
  return (
    <div className="flex flex-wrap gap-[2px] items-center">
      {shown.map(m => (
        <span key={m} className="inline-flex items-center gap-[3px] px-[6px] py-[1px] rounded-[10px] text-[10px] font-medium bg-[var(--color-info-bg,#e3f2fd)] text-[var(--color-info,#1565c0)]">
          <span className="w-[4px] h-[4px] rounded-full bg-current inline-block" />
          {m}
        </span>
      ))}
      {rest > 0 && (
        <span className="inline-flex items-center px-[6px] py-[1px] rounded-[10px] text-[10px] bg-[rgba(0,0,0,.06)] text-[var(--text-color-calendar-week)]">
          +{rest}
        </span>
      )}
      {shown.length === 0 && (
        <span className="opacity-30 text-[12px]">—</span>
      )}
    </div>
  );
}

function UserBar({ current, max, online }: { current: number; max?: number | null; online?: number }) {
  const bar = !max || max === 0 ? null : (() => {
    const pct = Math.min((current / max) * 100, 100);
    return (
      <div className="flex items-center gap-[7px] min-w-[90px]">
        <span className="text-[12px] min-w-[42px]">{current}/{max}</span>
        <div className="flex-1 h-[4px] rounded-[2px] bg-[rgba(0,0,0,.08)]">
          <div
            className={`h-[4px] rounded-[2px] ${pct >= 80 ? 'bg-[var(--color-warning,#e65100)]' : 'bg-[#1976d2]'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  })();
  return (
    <div className="flex flex-col gap-[2px]">
      {bar ?? <span className="text-[12px] text-[var(--text-color)]">{current} / ∞</span>}
      {!!online && online > 0 && (
        <span style={{ color: '#16a34a', fontSize: 10, lineHeight: 1 }}>● {online} online</span>
      )}
    </div>
  );
}

function LastLoginBadge({ date }: { date?: Date | string | null }) {
  if (!date) return <span className="opacity-30 text-[12px]">—</span>;
  const d = new Date(date as string);
  if (isNaN(d.getTime())) return <span className="opacity-30 text-[12px]">—</span>;
  const diffH = (Date.now() - d.getTime()) / 3600000;
  const color = diffH < 24 ? '#16a34a' : diffH < 168 ? '#d97706' : 'var(--text-color-calendar-week)';
  return (
    <div className="flex items-center gap-[5px]">
      <span style={{ color, fontSize: 9 }}>●</span>
      <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
        {moment(d).format('DD.MM.YY')}
      </span>
    </div>
  );
}

const CompanyTable: React.FC<Props> = ({
  companyRow, selected, setSelected, baseUrl, refreshCompany, tariffRow,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [openDel, setOpenDel] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const sorted = [...companyRow].sort((a, b) => {
    const va = a.title ?? '';
    const vb = b.title ?? '';
    return order === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });
  const paginated = sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(e.target.checked ? companyRow.map((c: any) => c.id) : []);
  };

  const handleSelect = (_e: React.MouseEvent, id: number) => {
    const idx = selected.indexOf(id);
    if (idx === -1) setSelected([...selected, id]);
    else setSelected(selected.filter(x => x !== id));
  };

  const getTariff = (id: number | null) =>
    tariffRow.find((t: any) => t.id === id) ?? null;

  const detailCompany = detailId ? companyRow.find((c: any) => c.id === detailId) ?? null : null;

  if (!Array.isArray(companyRow) || companyRow.length === 0) {
    return <EmptyState icon="🏢" message={t('No company available') as string} />;
  }

  return (
    <div className="flex gap-0 overflow-hidden">
      {/* TABLE SIDE */}
      <div className={`flex-1 min-w-0 mb-[10px] transition-all ${detailId ? 'overflow-x-auto' : ''}`}>
        {selected.length > 0 && (
          <div
            className="flex items-center justify-between py-[6px] px-[14px] rounded-[var(--radius-input)] mb-[4px]"
            style={{ background: 'rgba(37,99,235,.08)', border: '1px solid rgba(37,99,235,.15)' }}>
            <span className="text-[12px] font-semibold" style={{ color: '#2563eb' }}>{selected.length} {t('Selected')}</span>
            <button type="button" className="dt-btn-ghost" style={{ color: 'var(--color-danger,#dc2626)', fontSize: 12 }} onClick={() => setOpenDel(true)}>
              {t('Delete')}
            </button>
          </div>
        )}

        <TableContainer component={Paper} style={{ boxShadow: 'none', backgroundColor: 'inherit' }}>
          <Table sx={{ minWidth: detailId ? 600 : 900 }}>
            <TableHead>
              <TableRow>
                <StyledHeadCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selected.length > 0 && selected.length < companyRow.length}
                    checked={companyRow.length > 0 && selected.length === companyRow.length}
                    onChange={handleSelectAll}
                  />
                </StyledHeadCell>
                <StyledHeadCell sx={{ width: 32 }} />
                <StyledHeadCell>
                  <TableSortLabel
                    active
                    direction={order}
                    onClick={() => setOrder(o => o === 'asc' ? 'desc' : 'asc')}>
                    {t('Company')}
                  </TableSortLabel>
                </StyledHeadCell>
                {!detailId && <StyledHeadCell>{t('Tariff')}</StyledHeadCell>}
                <StyledHeadCell>{t('Users')}</StyledHeadCell>
                {!detailId && <StyledHeadCell>{t('Modules')}</StyledHeadCell>}
                <StyledHeadCell>{t('Paid until')}</StyledHeadCell>
                {!detailId && <StyledHeadCell>{t('Last Login')}</StyledHeadCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((company: any) => {
                const tariff = getTariff(company.tariffTypeId);
                const activeModules: string[] = company.modules_override
                  ? MODULES.filter(m => {
                      const ov = company.modules_override?.[m];
                      if (ov === true) return true;
                      if (ov === false) return false;
                      return tariff?.allowed_modules?.includes(m) ?? false;
                    })
                  : (tariff?.allowed_modules ?? []);
                const isSelected = selected.indexOf(company.id) !== -1;
                const isOpen = detailId === company.id;

                return (
                  <TableRow
                    key={company.id}
                    hover
                    selected={isOpen}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected td': { backgroundColor: 'rgba(25,118,210,.07) !important' },
                    }}
                    onClick={() => setDetailId(isOpen ? null : company.id)}>
                    <StyledBodyCell padding="checkbox" onClick={e => { e.stopPropagation(); handleSelect(e, company.id); }}>
                      <Checkbox size="small" checked={isSelected} />
                    </StyledBodyCell>

                    {/* Logo / Avatar */}
                    <StyledBodyCell sx={{ padding: '7px 6px', width: 32 }}>
                      {company.logoUrl ? (
                        <img src={`${baseUrl}${company.logoUrl}`} alt={company.title} className="h-[22px] rounded-[3px]" />
                      ) : (
                        <div
                          className="w-[24px] h-[24px] rounded-[4px] flex items-center justify-center text-white text-[9px] font-bold"
                          style={{ background: stringToColor(company.title) }}>
                          {(company.title ?? '?').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </StyledBodyCell>

                    {/* Name */}
                    <StyledBodyCell>
                      <div className="font-semibold text-[13px]">{company.title}</div>
                    </StyledBodyCell>

                    {/* Tariff badge */}
                    {!detailId && (
                      <StyledBodyCell>
                        {tariff ? (
                          <span className="inline-block px-[7px] py-[1px] rounded-[10px] text-[11px] font-medium bg-[var(--color-info-bg,#e3f2fd)] text-[var(--color-info,#1565c0)]">
                            {tariff.name}
                          </span>
                        ) : (
                          <span className="opacity-30 text-[12px]">—</span>
                        )}
                      </StyledBodyCell>
                    )}

                    {/* Users */}
                    <StyledBodyCell>
                      <UserBar current={company.users_number ?? 0} max={tariff?.max_users} online={company.online_count ?? 0} />
                    </StyledBodyCell>

                    {/* Modules */}
                    {!detailId && (
                      <StyledBodyCell>
                        <ModuleChips modules={activeModules} tariff={tariff} />
                      </StyledBodyCell>
                    )}

                    {/* Paid until */}
                    <StyledBodyCell>
                      <PaidUntilBadge date={company.paid_until} />
                    </StyledBodyCell>

                    {/* Last login */}
                    {!detailId && (
                      <StyledBodyCell>
                        <LastLoginBadge date={company.last_login} />
                      </StyledBodyCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="px-[15px] py-[5px] flex justify-between items-center bg-[var(--bg-top-fix)]">
          <Paginations
            rows={companyRow}
            rowsPerPage={rowsPerPage}
            page={page}
            setRowsPerPage={setRowsPerPage}
            setPage={setPage}
            arrayPages={[10, 20, 50]}
          />
          <div className="flex items-center gap-[5px] text-[var(--text-color-calendar-week)] text-[12px]">
            <span>{companyRow.length}</span>
            <FontAwesomeIcon icon={faBuilding} className="opacity-30" />
          </div>
        </div>
      </div>

      {/* DETAIL PANEL */}
      {detailId && detailCompany && (
        <CompanyDetail
          company={detailCompany}
          tariffRow={tariffRow}
          onClose={() => setDetailId(null)}
          onRefresh={refreshCompany}
        />
      )}

      {openDel && (
        <DeleteArrayAll
          open={openDel}
          setOpen={setOpenDel}
          refresh={refreshCompany}
          selectedArrayId={selected}
          setSelected={setSelected}
          wordMenu="company"
          pd="company"
        />
      )}
    </div>
  );
};

function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = hash % 360;
  return `hsl(${h},50%,45%)`;
}

export default CompanyTable;
